import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Images as ImagesIcon, Upload as UploadIcon, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { api, isImageResult, isStockResult, uploadAndIngest } from "@/api/client";
import { IngestionJobsCard } from "@/components/ingestion-jobs-card";
import { refusalNote } from "@/lib/zeroing-refusal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// A file is required unless the supplier is in `pullSuppliers`, which comes
// from the API.
function buildIngestionSchema(pullSuppliers: string[]) {
  return z
    .object({
      supplier: z.string().min(1, "Select a supplier."),
      file: z.instanceof(File).optional(),
    })
    .refine((values) => pullSuppliers.includes(values.supplier) || values.file !== undefined, {
      path: ["file"],
      message: "Select a file.",
    });
}
type IngestionValues = z.infer<ReturnType<typeof buildIngestionSchema>>;

const TERMINAL_STATES = new Set(["completed", "failed"]);

function JobStatusCard({ jobId }: { jobId: string }) {
  const { data } = useQuery({
    queryKey: ["ingestion-job", jobId],
    queryFn: () => api.ingestionJobStatus(jobId),
    refetchInterval: (query) => (TERMINAL_STATES.has(query.state.data?.state ?? "") ? false : 2000),
  });

  useEffect(() => {
    if (data?.state === "completed") {
      const result = data.returnValue;
      if (result && isImageResult(result)) {
        toast.success(
          `Images synced: ${result.imagesDownloaded} downloaded, ${result.imageErrors} errors`,
        );
      } else if (result && isStockResult(result)) {
        toast.success(
          `Ingestion completed: ${result.rowsProcessed} rows, ${result.rowErrors} errors`,
        );
      }
    } else if (data?.state === "failed") {
      toast.error(data.failedReason ?? "The ingestion failed.");
    }
  }, [data?.state, data?.returnValue, data?.failedReason]);

  if (!data) return null;

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Job {data.id}</span>
        <Badge variant={data.state === "failed" ? "destructive" : "secondary"} className="gap-1.5">
          {data.state === "completed" && <CheckCircle2 className="size-3.5" />}
          {data.state === "failed" && <XCircle className="size-3.5" />}
          {data.state}
        </Badge>
      </div>
      {data.state === "completed" &&
        data.returnValue &&
        (isImageResult(data.returnValue) ? (
          <p className="text-sm text-muted-foreground">
            {data.returnValue.imagesDownloaded} images downloaded and{" "}
            {data.returnValue.imagesReused} reused for{" "}
            {data.returnValue.itemsFoundInFeed} of {data.returnValue.itemsTargeted} items
            looked up. {data.returnValue.priceImagesLinked} linked to prices,{" "}
            {data.returnValue.productImagesLinked} to products.
            {data.returnValue.skippedGeneric > 0 &&
              ` ${data.returnValue.skippedGeneric} skipped as generic images.`}
            {data.returnValue.imageErrors > 0 && ` ${data.returnValue.imageErrors} errors.`}
            {data.returnValue.stoppedAtLimit &&
              " The per-run limit was reached: run it again to continue."}
          </p>
        ) : isStockResult(data.returnValue) ? (
          <p className="text-sm text-muted-foreground">
            {data.returnValue.rowsProcessed} rows processed, {data.returnValue.rowErrors} errors.
            {data.returnValue.imagesInserted > 0 &&
              ` ${data.returnValue.imagesInserted} new images, ${data.returnValue.priceImagesLinked} linked.`}
            {data.returnValue.pricesZeroed > 0 &&
              ` ${data.returnValue.pricesZeroed} prices zeroed for being absent from the feed.`}
            {data.returnValue.zeroingRefused && (
              <span className="block text-destructive">
                {refusalNote(data.returnValue.zeroingRefused)}
              </span>
            )}
          </p>
        ) : null)}
      {data.state === "failed" && data.failedReason && (
        <p className="text-sm text-destructive">{data.failedReason}</p>
      )}
    </div>
  );
}

export function IngestionPage() {
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  // Bumped to remount the file input: it is uncontrolled, so resetField()
  // alone leaves the old file name showing.
  const [fileInputKey, setFileInputKey] = useState(0);

  const suppliersQuery = useQuery({ queryKey: ["ingestion-suppliers"], queryFn: api.suppliers });
  const pullSuppliers = suppliersQuery.data?.pullSuppliers ?? [];
  const imageSuppliers = suppliersQuery.data?.imageSuppliers ?? [];

  const form = useForm<IngestionValues>({
    resolver: zodResolver(buildIngestionSchema(pullSuppliers)),
    defaultValues: { supplier: "", file: undefined },
  });

  const selectedFile = form.watch("file");
  const selectedSupplier = form.watch("supplier");
  const isPullSupplier = pullSuppliers.includes(selectedSupplier);
  const isImageSupplier = imageSuppliers.includes(selectedSupplier);

  const uploadMutation = useMutation({
    mutationFn: (values: IngestionValues) => {
      if (values.file === undefined) return api.triggerIngestion(values.supplier);
      setUploadProgress(0);
      return uploadAndIngest(values.supplier, values.file, setUploadProgress);
    },
    onSuccess: (result) => {
      setJobId(result.jobId);
      if ("enqueued" in result && !result.enqueued) {
        toast.info(`An ingestion was already running (job ${result.jobId})`);
      } else {
        toast.success(`Ingestion queued (job ${result.jobId})`);
      }
      form.resetField("file");
      setFileInputKey((key) => key + 1);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Could not start the ingestion.");
    },
    onSettled: () => setUploadProgress(null),
  });

  const imageMutation = useMutation({
    mutationFn: () => api.triggerImageSync(selectedSupplier),
    onSuccess: (result) => {
      setJobId(result.jobId);
      if (!result.enqueued) {
        toast.info(`An image sync was already running (job ${result.jobId})`);
      } else {
        toast.success(`Image sync queued (job ${result.jobId})`);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Could not start the image sync.",
      );
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    setJobId(null);
    uploadMutation.mutate(values);
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Catalogue ingestion</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {/* Top-aligned so a field's error does not shift the others; the button
                and pull note sit under an empty label so they line up with the
                inputs. */}
            <div className="grid items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              <div className="space-y-1.5">
                <Label htmlFor="supplier">Supplier</Label>
                <Controller
                  control={form.control}
                  name="supplier"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={(supplier) => {
                        field.onChange(supplier);
                        // The file input is hidden for a pull supplier; a file
                        // left picked would be uploaded unseen.
                        if (pullSuppliers.includes(supplier)) {
                          form.resetField("file");
                          setFileInputKey((key) => key + 1);
                        }
                      }}
                      disabled={suppliersQuery.isLoading}
                      onOpenChange={(open) => {
                        // Radix Select can leave `pointer-events: none` on <body>
                        // after closing, blocking clicks on the form
                        // (radix-ui/primitives#2122).
                        if (!open) {
                          setTimeout(() => {
                            document.body.style.pointerEvents = "";
                          }, 0);
                        }
                      }}
                    >
                      <SelectTrigger id="supplier">
                        <SelectValue placeholder="Select a supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliersQuery.data?.suppliers.map((supplier) => (
                          <SelectItem key={supplier} value={supplier}>
                            {supplier}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {suppliersQuery.isError && (
                  <p className="text-sm text-destructive">
                    Could not load the suppliers
                    {suppliersQuery.error instanceof Error
                      ? `: ${suppliersQuery.error.message}`
                      : "."}
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 pl-1 text-sm"
                      onClick={() => suppliersQuery.refetch()}
                    >
                      Retry
                    </Button>
                  </p>
                )}
                {suppliersQuery.isSuccess && suppliersQuery.data.suppliers.length === 0 && (
                  <p className="text-sm text-muted-foreground">No suppliers configured.</p>
                )}
                {form.formState.errors.supplier && (
                  <p className="text-sm text-destructive">{form.formState.errors.supplier.message}</p>
                )}
              </div>

              {isPullSupplier ? (
                <div className="space-y-1.5">
                  <LabelSpacer />
                  <p className="flex min-h-10 items-center text-sm text-muted-foreground">
                    {selectedSupplier} needs no file: the API downloads its feed when the
                    ingestion starts.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="file">File</Label>
                  <Controller
                    control={form.control}
                    name="file"
                    render={({ field }) => (
                      <Input
                        key={fileInputKey}
                        id="file"
                        type="file"
                        className="py-2"
                        accept=".csv,.json,.xlsx,.xls"
                        name={field.name}
                        ref={field.ref}
                        onBlur={field.onBlur}
                        onChange={(event) => field.onChange(event.target.files?.[0])}
                      />
                    )}
                  />
                  {selectedFile && (
                    <p className="text-xs text-muted-foreground">
                      {selectedFile.name} ({formatBytes(selectedFile.size)})
                    </p>
                  )}
                  {form.formState.errors.file && (
                    <p className="text-sm text-destructive">{form.formState.errors.file.message}</p>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <LabelSpacer />
                <Button type="submit" disabled={uploadMutation.isPending} className="w-full">
                  <UploadIcon className="size-4" />
                  {uploadMutation.isPending ? "Processing..." : "Start ingestion"}
                </Button>
              </div>
            </div>

            {uploadProgress !== null && (
              <div className="space-y-1.5">
                <Progress value={uploadProgress} />
                <p className="text-xs text-muted-foreground">Uploading... {uploadProgress}%</p>
              </div>
            )}

            {isImageSupplier && (
              <div className="space-y-1.5 border-t pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={imageMutation.isPending}
                  onClick={() => {
                    setJobId(null);
                    imageMutation.mutate();
                  }}
                >
                  <ImagesIcon className="size-4" />
                  {imageMutation.isPending ? "Queuing..." : "Sync images"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Downloads {selectedSupplier}'s image feed and looks for photos for the prices
                  that still have none. It is slow (one download per image) and can be repeated:
                  each run picks up where the previous one left off.
                </p>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {jobId && (
        <Card>
          <CardHeader>
            <CardTitle>Job status</CardTitle>
            <CardDescription>Progress of the queued ingestion</CardDescription>
          </CardHeader>
          <CardContent>
            <JobStatusCard jobId={jobId} />
          </CardContent>
        </Card>
      )}

      <IngestionJobsCard />
    </div>
  );
}

// Takes a label's space in the form row, so what follows lines up with the
// labelled inputs. Only on the wide layout, where the fields share a row.
function LabelSpacer() {
  return (
    <Label aria-hidden="true" className="invisible hidden md:inline">
      &nbsp;
    </Label>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(1)} ${units[unit]}`;
}
